import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export function DateRangePicker({
  onDateRangeChange,
  startDate = '',
  endDate = '',
  className,
  placeholder = 'Seleccionar período'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const dropdownRef = useRef(null);

  // Cerrar dropdown al hacer clic fuera o presionar Escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen]);

  // Sincronizar con props externas
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  const parseYmdToLocal = (value) => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, 12, 0, 0);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setTempStartDate('');
    setTempEndDate('');
    onDateRangeChange('', '');
  };

  const handleApply = () => {
    onDateRangeChange(tempStartDate, tempEndDate);
    setIsOpen(false);
  };

  const formatDateRange = () => {
    if (!startDate && !endDate) {
      return placeholder;
    }

    if (startDate && endDate) {
      const startValue = parseYmdToLocal(startDate);
      const endValue = parseYmdToLocal(endDate);
      const start = (startValue || new Date()).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short'
      });
      const end = (endValue || new Date()).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short'
      });
      return `${start} - ${end}`;
    }

    if (startDate) {
      const startValue = parseYmdToLocal(startDate);
      const start = (startValue || new Date()).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short'
      });
      return `Desde ${start}`;
    }

    if (endDate) {
      const endValue = parseYmdToLocal(endDate);
      const end = (endValue || new Date()).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short'
      });
      return `Hasta ${end}`;
    }

    return placeholder;
  };

  const tempStartValue = parseYmdToLocal(tempStartDate);
  const tempEndValue = parseYmdToLocal(tempEndDate);
  const isValidRange = tempStartDate && tempEndDate && tempStartValue && tempEndValue && tempStartValue <= tempEndValue;
  const hasChanges = tempStartDate !== startDate || tempEndDate !== endDate;

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          (startDate || endDate) ? "text-foreground" : "text-muted-foreground"
        )}
      >
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4" />
          <span>{formatDateRange()}</span>
        </div>
        <div className="flex items-center space-x-1">
          {(startDate || endDate) && (
            <X
              className="h-4 w-4 opacity-50 hover:opacity-100"
              onClick={handleClear}
            />
          )}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-11 left-0 z-50 w-full min-w-[320px] bg-background border border-border rounded-md shadow-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">
                  Fecha de inicio
                </label>
                <input
                  type="date"
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                  className="w-full h-9 px-3 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                  style={{ colorScheme: 'dark' }}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">
                  Fecha de fin
                </label>
                <input
                  type="date"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  className="w-full h-9 px-3 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                  style={{ colorScheme: 'dark' }}
                  min={tempStartDate || undefined}
                />
              </div>

              {tempStartDate && tempEndDate && !isValidRange && (
                <div className="text-xs text-destructive">
                  La fecha de fin debe ser posterior a la fecha de inicio
                </div>
              )}
            </div>
          </div>

          <div className="p-3 flex justify-between items-center">
            <button
              type="button"
              onClick={() => {
                setTempStartDate('');
                setTempEndDate('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Limpiar todo
            </button>

            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-3 py-1.5 text-xs bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={!isValidRange && (tempStartDate || tempEndDate)}
                className="px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Aplicar{hasChanges && isValidRange ? ` (${tempStartDate && tempEndDate ? '2 fechas' : '1 fecha'})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

